import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Heart, Users, Globe, Award, MapPin, TrendingUp, ShieldCheck } from 'lucide-react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Card from '../components/ui/Card';
import { Container, Section, SectionLabel, Eyebrow, Lede, PageLoading } from '../components/ui/Section';
import { useSiteContent } from '../hooks/useSiteContent';

gsap.registerPlugin(ScrollTrigger);

const PageHeader = styled.section`
  background: ${props => props.theme.colors.background};
  padding: ${props => props.theme.sectionPadding.md} 0 ${props => props.theme.sectionPadding.sm};
  border-bottom: 1px solid ${props => props.theme.colors.border};
`;

const PageTitle = styled(motion.h1)`
  max-width: 16ch;
  margin-bottom: ${props => props.theme.spacing[6]};
`;

const PageSubtitle = styled(motion(Lede))`
  margin: 0;
`;

const SectionTitle = styled.h2`
  max-width: 20ch;
  margin-bottom: ${props => props.theme.spacing[12]};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${props => props.theme.spacing[5]};

  @media (min-width: ${props => props.theme.breakpoints.md}) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const ValueCard = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing[4]};

  svg {
    color: ${props => props.theme.colors.primary[500]};
    width: 22px;
    height: 22px;
  }

  h3 {
    font-size: ${props => props.theme.typography.fontSize.xl};
    color: ${props => props.theme.colors.ink};
    margin: 0;
  }

  p {
    color: ${props => props.theme.colors.textMuted};
    line-height: ${props => props.theme.typography.lineHeight.relaxed};
    margin: 0;
  }
`;

const StorySection = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${props => props.theme.spacing[10]};
  align-items: center;

  @media (min-width: ${props => props.theme.breakpoints.lg}) {
    grid-template-columns: 1fr 1fr;
    gap: ${props => props.theme.spacing[16]};
  }
`;

const StoryImage = styled.img`
  width: 100%;
  height: 460px;
  object-fit: cover;
  object-position: center;
  border-radius: ${props => props.theme.borderRadius.lg};
`;

const StoryContent = styled.div`
  h3 {
    font-size: ${props => props.theme.typography.fontSize['3xl']};
    margin-bottom: ${props => props.theme.spacing[6]};
    color: ${props => props.theme.colors.ink};
    max-width: 18ch;
  }

  p {
    color: ${props => props.theme.colors.textMuted};
    line-height: ${props => props.theme.typography.lineHeight.relaxed};

    & + p {
      margin-top: ${props => props.theme.spacing[5]};
    }
  }
`;

// Icon mapping helper
const getIconComponent = (iconName: string) => {
  const icons: { [key: string]: any } = {
    'heart': Heart,
    'users': Users,
    'globe': Globe,
    'award': Award,
    'map-pin': MapPin,
    'trending-up': TrendingUp,
    'shield-check': ShieldCheck,
  };
  return icons[iconName] || Heart;
};

const About: React.FC = () => {
  const { siteContent, isLoading } = useSiteContent();
  const aboutPage = siteContent?.aboutPage;

  const storyImageRef = useRef<HTMLImageElement>(null);
  const storyContentRef = useRef<HTMLDivElement>(null);
  const valueCardsRef = useRef<HTMLDivElement>(null);
  const approachCardsRef = useRef<HTMLDivElement>(null);
  const missionSectionRef = useRef<HTMLDivElement>(null);
  const visionSectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isLoading || !aboutPage) return;

    // Story section animations
    if (storyImageRef.current) {
      gsap.fromTo(storyImageRef.current,
        { opacity: 0.5, x: -15 },
        {
          opacity: 1,
          x: 0,
          duration: 0.6,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: storyImageRef.current,
            start: 'top 80%',
            toggleActions: 'play none none none'
          }
        }
      );
    }

    if (storyContentRef.current) {
      gsap.fromTo(storyContentRef.current,
        { opacity: 0.5, x: 15 },
        {
          opacity: 1,
          x: 0,
          duration: 0.6,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: storyContentRef.current,
            start: 'top 80%',
            toggleActions: 'play none none none'
          }
        }
      );
    }

    // Value cards stagger animation
    if (valueCardsRef.current) {
      const cards = valueCardsRef.current.children;
      gsap.fromTo(cards,
        { opacity: 0.5, y: 15 },
        {
          opacity: 1,
          y: 0,
          duration: 0.6,
          stagger: 0.1,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: valueCardsRef.current,
            start: 'top 75%',
            toggleActions: 'play none none none'
          }
        }
      );
    }

    // Approach cards stagger animation
    if (approachCardsRef.current) {
      const cards = approachCardsRef.current.children;
      gsap.fromTo(cards,
        { opacity: 0.5, y: 15 },
        {
          opacity: 1,
          y: 0,
          duration: 0.6,
          stagger: 0.1,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: approachCardsRef.current,
            start: 'top 75%',
            toggleActions: 'play none none none'
          }
        }
      );
    }

    // Mission section
    if (missionSectionRef.current) {
      const content = missionSectionRef.current.querySelector('.story-content');
      const image = missionSectionRef.current.querySelector('.story-image');

      if (content) {
        gsap.fromTo(content,
          { opacity: 0.5, x: -15 },
          {
            opacity: 1,
            x: 0,
            duration: 0.6,
            ease: 'power2.out',
            scrollTrigger: {
              trigger: content,
              start: 'top 80%',
              toggleActions: 'play none none none'
            }
          }
        );
      }

      if (image) {
        gsap.fromTo(image,
          { opacity: 0.5, x: 15 },
          {
            opacity: 1,
            x: 0,
            duration: 0.6,
            ease: 'power2.out',
            scrollTrigger: {
              trigger: image,
              start: 'top 80%',
              toggleActions: 'play none none none'
            }
          }
        );
      }
    }

    // Vision section
    if (visionSectionRef.current) {
      const image = visionSectionRef.current.querySelector('.story-image');
      const content = visionSectionRef.current.querySelector('.story-content');

      if (image) {
        gsap.fromTo(image,
          { opacity: 0.5, x: -15 },
          {
            opacity: 1,
            x: 0,
            duration: 0.6,
            ease: 'power2.out',
            scrollTrigger: {
              trigger: image,
              start: 'top 80%',
              toggleActions: 'play none none none'
            }
          }
        );
      }

      if (content) {
        gsap.fromTo(content,
          { opacity: 0.5, x: 15 },
          {
            opacity: 1,
            x: 0,
            duration: 0.6,
            ease: 'power2.out',
            scrollTrigger: {
              trigger: content,
              start: 'top 80%',
              toggleActions: 'play none none none'
            }
          }
        );
      }
    }

    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, [isLoading, aboutPage]);

  if (isLoading || !aboutPage) {
    return <PageLoading />;
  }

  return (
    <div>
      <PageHeader>
        <Container>
          <Eyebrow>About us</Eyebrow>
          <PageTitle
            initial={{ opacity: 0.5, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
          >
            {aboutPage.pageTitle}
          </PageTitle>
          <PageSubtitle
            initial={{ opacity: 0.5, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15, ease: 'easeOut' }}
          >
            {aboutPage.pageSubtitle}
          </PageSubtitle>
        </Container>
      </PageHeader>

      <Section $size="lg">
        <Container>
          <SectionLabel>Our story</SectionLabel>
          <StorySection>
            <StoryImage
              ref={storyImageRef}
              src={aboutPage.story.imageUrl}
              alt="Our story"
            />
            <StoryContent ref={storyContentRef}>
              <h3>{aboutPage.story.title}</h3>
              {aboutPage.story.paragraphs.map((paragraph, index) => (
                <p key={index}>{paragraph}</p>
              ))}
            </StoryContent>
          </StorySection>
        </Container>
      </Section>

      {aboutPage.firstInitiative && (
        <Section $surface="muted" $size="lg">
          <Container>
            <SectionLabel align="left">Where it started</SectionLabel>
            <StorySection>
              <StoryContent>
                <h3>{aboutPage.firstInitiative.title}</h3>
                {aboutPage.firstInitiative.paragraphs.map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))}
              </StoryContent>
              <StoryImage
                src={aboutPage.firstInitiative.imageUrl}
                alt="Our first initiative"
              />
            </StorySection>
          </Container>
        </Section>
      )}

      {aboutPage.focusAreas && (
        <Section $size="lg">
          <Container>
            <SectionLabel>What we work on</SectionLabel>
            <SectionTitle>Our focus areas</SectionTitle>
            <Grid ref={valueCardsRef}>
              {aboutPage.focusAreas.map((area, index) => {
                const IconComponent = getIconComponent(area.icon);
                return (
                  <ValueCard key={index} padding="lg">
                    <IconComponent size={22} />
                    <h3>{area.title}</h3>
                    <p>{area.description}</p>
                  </ValueCard>
                );
              })}
            </Grid>
          </Container>
        </Section>
      )}

      {aboutPage.approach && (
        <Section $surface="muted" $size="lg">
          <Container>
            <SectionLabel align="left">How we work</SectionLabel>
            <SectionTitle>Our approach</SectionTitle>
            <Grid ref={approachCardsRef}>
              {aboutPage.approach.map((item, index) => {
                const IconComponent = getIconComponent(item.icon);
                return (
                  <ValueCard key={index} padding="lg">
                    <IconComponent size={22} />
                    <h3>{item.title}</h3>
                    <p>{item.description}</p>
                  </ValueCard>
                );
              })}
            </Grid>
          </Container>
        </Section>
      )}

      <Section ref={missionSectionRef} $size="lg">
        <Container>
          <SectionLabel>Mission</SectionLabel>
          <StorySection>
            <StoryContent className="story-content">
              <h3>{aboutPage.mission.title}</h3>
              {aboutPage.mission.paragraphs.map((paragraph, index) => (
                <p key={index}>{paragraph}</p>
              ))}
            </StoryContent>
            <StoryImage
              className="story-image"
              src={aboutPage.mission.imageUrl}
              alt="Our mission"
            />
          </StorySection>
        </Container>
      </Section>

      <Section ref={visionSectionRef} $surface="muted" $size="lg">
        <Container>
          <SectionLabel align="left">Vision</SectionLabel>
          <StorySection>
            <StoryImage
              className="story-image"
              src={aboutPage.vision.imageUrl}
              alt="Our vision"
            />
            <StoryContent className="story-content">
              <h3>{aboutPage.vision.title}</h3>
              {aboutPage.vision.paragraphs.map((paragraph, index) => (
                <p key={index}>{paragraph}</p>
              ))}
            </StoryContent>
          </StorySection>
        </Container>
      </Section>
    </div>
  );
};

export default About;